import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  CreditCard, Package, UsersThree, ArrowClockwise, MagnifyingGlass,
  UserPlus, Plus, Trash, X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import SearchBar from '../components/SearchBar';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export default function CardStockPage() {
  const [stats, setStats] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('available');
  const [filterStaff, setFilterStaff] = useState('');
  const [filterAgency, setFilterAgency] = useState('');
  const [search, setSearch] = useState('');
  const [staffList, setStaffList] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [rowAssignments, setRowAssignments] = useState({});  // { [cardId]: {id, name} }

  // Dialogs
  const [showUploadDlg, setShowUploadDlg] = useState(false);
  const [upMode, setUpMode] = useState('manual');  // 'manual' | 'excel'
  const [upText, setUpText] = useState('');
  const [upFile, setUpFile] = useState(null);
  const [upPreview, setUpPreview] = useState(null);
  const [upBusy, setUpBusy] = useState(false);
  const [lastUpload, setLastUpload] = useState(null);  // post-upload tracking

  const [showAssignDlg, setShowAssignDlg] = useState(false);
  const [assignStaffInput, setAssignStaffInput] = useState('');
  const [assignStaff, setAssignStaff] = useState(null);
  // Item 5: agency soft-lock
  const [agencies, setAgencies] = useState([]);
  const [upLinkedAgency, setUpLinkedAgency] = useState('');  // agency_id or ''
  const [upPassword, setUpPassword] = useState('');         // card login password

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterStaff) params.set('staff_id', filterStaff);
      if (filterAgency) params.set('agency_id', filterAgency);
      if (search) params.set('search', search);
      const [s, l, st, ag] = await Promise.all([
        api.get('/card-stock/stats'),
        api.get(`/card-stock/?${params.toString()}`),
        api.get('/card-stock/staff-list'),
        api.get('/card-stock/agencies-list').catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setCards(l.data || []);
      setStaffList(st.data || []);
      setAgencies(ag.data || []);
      // Seed inline-assign state
      const seed = {};
      (l.data || []).forEach(c => {
        if (c.assigned_to_id) seed[c.id] = { id: c.assigned_to_id, name: c.assigned_to_name };
      });
      setRowAssignments(seed);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [filterStatus, filterStaff, filterAgency, search]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const exportExcel = async () => {
    const params = new URLSearchParams();
    if (filterStatus !== 'all') params.set('status', filterStatus);
    if (filterStaff) params.set('staff_id', filterStaff);
    if (filterAgency) params.set('agency_id', filterAgency);
    if (search) params.set('search', search);
    try {
      const r = await api.get(`/card-stock/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      const suffix = filterAgency ? `_${filterAgency.slice(0, 8)}` : '';
      a.download = `CardStock_${filterStatus}${suffix}_${Date.now()}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel downloaded');
    } catch (err) { toast.error('Export failed'); }
  };
  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(s => s.size === cards.length ? new Set() : new Set(cards.map(c => c.id)));

  const submitPreview = async () => {
    if (!upText.trim()) { toast.error('Card text paste karo'); return; }
    setUpBusy(true);
    try {
      const fd = new FormData();
      fd.append('raw_text', upText);
      const r = await api.post('/card-stock/parse-preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUpPreview(r.data);
      if (!r.data.count) toast.error('Koi card parse nahi hua');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Parse failed');
    } finally { setUpBusy(false); }
  };

  const submitUpload = async () => {
    if (upMode === 'manual' && !upText.trim()) { toast.error('Card text paste karo'); return; }
    if (upMode === 'excel' && !upFile) { toast.error('Excel file select karo'); return; }
    setUpBusy(true);
    try {
      const fd = new FormData();
      if (upMode === 'excel') fd.append('file', upFile);
      else fd.append('raw_text', upText);
      if (upLinkedAgency) {
        const ag = agencies.find(a => a.id === upLinkedAgency);
        fd.append('linked_agency_id', upLinkedAgency);
        if (ag?.name) fd.append('linked_agency_name', ag.name);
      }
      if (upPassword) fd.append('card_password', upPassword);
      const r = await api.post('/card-stock/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = r.data || {};
      if (d.inserted > 0) toast.success(d.message);
      else if (d.skipped_existing > 0) toast.warning(d.message + ' Saare cards pehle se DB me hain.');
      else toast.error('Kuch insert nahi hua: ' + (d.message || 'unknown'));
      setLastUpload({ ...d, uploaded_at: new Date().toISOString() });
      setShowUploadDlg(false);
      setUpText(''); setUpFile(null); setUpPreview(null);
      setUpLinkedAgency(''); setUpPassword('');
      setFilterStatus('available');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUpBusy(false); }
  };

  const submitAssign = async () => {
    if (!assignStaff) { toast.error('Staff select karo'); return; }
    if (selected.size === 0) { toast.error('Cards select karo'); return; }
    try {
      const r = await api.post('/card-stock/assign', {
        card_ids: Array.from(selected),
        staff_id: assignStaff.id,
      });
      toast.success(r.data.message);
      setShowAssignDlg(false); setSelected(new Set());
      setAssignStaff(null); setAssignStaffInput('');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Assign failed');
    }
  };

  const doInlineAssign = async (cardId, staff) => {
    if (!staff?.id) return;
    try {
      const r = await api.post('/card-stock/assign', { card_ids: [cardId], staff_id: staff.id });
      toast.success(r.data.message);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
  };

  const doUnassign = async (cardId) => {
    if (!window.confirm('Card admin pool me wapis lana hai?')) return;
    try {
      await api.post('/card-stock/unassign', { card_ids: [cardId], staff_id: '' });
      toast.success('Unassigned');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const doDelete = async (cardId) => {
    if (!window.confirm('Card delete kar du? (soft delete)')) return;
    try {
      await api.delete(`/card-stock/${cardId}`);
      toast.success('Deleted');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const doAdjustBalance = async (card) => {
    const cur = Number(card.available_limit || 0);
    const input = window.prompt(
      `Card **** ${card.last4} balance adjust karein.\n` +
      `Current balance: ₹${cur.toLocaleString('en-IN')}\n` +
      `Initial limit: ₹${Number(card.initial_limit || 0).toLocaleString('en-IN')}\n\n` +
      `Naya balance daalein (absolute value):`,
      String(cur)
    );
    if (input === null) return;
    const newBal = parseFloat(input);
    if (isNaN(newBal) || newBal < 0) { toast.error('Invalid balance'); return; }
    const note = window.prompt('Note / reason (optional):', '') || '';
    try {
      await api.patch(`/card-stock/${card.id}/adjust-balance`, {
        available_limit: newBal,
        note,
      });
      toast.success(`Balance updated to ₹${newBal.toLocaleString('en-IN')}`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const doSetCardPassword = async (card) => {
    const cur = card.card_password || '';
    const input = window.prompt(
      `Card **** ${card.last4} ka login password set karein.\n` +
      `(Online checkout / vendor portal me login ke liye)\n\n` +
      `Current: ${cur || '(not set)'}\n\n` +
      `Naya password (khali chhodne se hat jayega):`,
      cur
    );
    if (input === null) return;
    try {
      await api.patch(`/card-stock/${card.id}/password`, { card_password: input });
      toast.success(input ? `Password set for ****${card.last4}` : `Password cleared for ****${card.last4}`);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const filteredStaff = (q) => {
    const s = (q || '').toLowerCase().trim();
    if (!s) return staffList.slice(0, 8);
    return staffList.filter(st => st.name.toLowerCase().includes(s) || st.email.toLowerCase().includes(s)).slice(0, 8);
  };

  return (
    <div className="space-y-5" data-testid="card-stock-page">
      {/* Header */}
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CreditCard size={26} weight="duotone" className="text-violet-600" /> Card Stock
        </h1>
        <span className="text-sm text-gray-500">Admin payment cards main staff ko assign karein — staff apni team ko distribute karega</span>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowUploadDlg(true)} className="bg-violet-50 border-violet-300 text-violet-700 hover:bg-violet-100" data-testid="upload-cards-btn">
            <Plus size={14} /> Upload Cards
          </Button>
          <Button variant="outline" size="sm" onClick={loadAll}>
            <ArrowClockwise size={14} /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard color="emerald" icon={<Package size={18} />} label="Available" count={stats.by_status.available?.count || 0} sub={fmt(stats.by_status.available?.balance_total || 0) + ' balance'} />
          <StatCard color="blue" icon={<UsersThree size={18} />} label="Assigned to Staff" count={stats.by_status.assigned?.count || 0} sub={fmt(stats.by_status.assigned?.balance_total || 0) + ' balance'} />
          <StatCard color="gray" icon={<CreditCard size={18} />} label="Exhausted" count={stats.by_status.exhausted?.count || 0} sub={fmt(stats.by_status.exhausted?.limit_total || 0) + ' used'} />
          <StatCard color="red" icon={<X size={18} />} label="Blocked" count={stats.by_status.blocked?.count || 0} sub={fmt(stats.by_status.blocked?.limit_total || 0) + ' limit'} />
        </div>
      )}

      {/* Last Upload — Live Tracking */}
      {lastUpload && (
        <div className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border-2 border-violet-300 rounded-lg p-3" data-testid="last-upload-tracker">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <div className="text-xs font-bold text-violet-700 uppercase">Just Uploaded · Live Tracking</div>
              <div className="text-2xl font-bold text-violet-900 mt-1">
                {lastUpload.inserted} cards added
                <span className="text-sm font-normal text-violet-700 ml-2">{fmt(lastUpload.total_face)} total face</span>
              </div>
              {lastUpload.skipped_existing > 0 && (
                <div className="text-xs text-amber-700 mt-1">⚠️ {lastUpload.skipped_existing} duplicates auto-skipped</div>
              )}
              {lastUpload.skipped_within_batch > 0 && (
                <div className="text-xs text-amber-700">⚠️ {lastUpload.skipped_within_batch} batch-dupes</div>
              )}
            </div>
            <button onClick={() => setLastUpload(null)} className="text-violet-600 hover:bg-violet-200 rounded p-1"><X size={16} /></button>
          </div>
          {lastUpload.inserted_last4?.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Available (not yet assigned):</div>
              <div className="flex flex-wrap gap-1">
                {lastUpload.inserted_last4.map((l, i) => (
                  <span key={i} className="px-2 py-0.5 bg-emerald-100 border border-emerald-300 rounded text-[11px] font-mono">****{l}</span>
                ))}
              </div>
            </div>
          )}
          {lastUpload.skipped_existing_last4?.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase font-semibold text-amber-700 mb-1">Skipped (already in DB):</div>
              <div className="flex flex-wrap gap-1">
                {lastUpload.skipped_existing_last4.map((l, i) => (
                  <span key={i} className="px-2 py-0.5 bg-amber-100 border border-amber-300 rounded text-[11px] font-mono">****{l}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Per-Agency Allocation */}
      {stats?.per_agency?.length > 0 && (
        <div className="bg-white border rounded-lg p-3" data-testid="per-agency-widget">
          <div className="text-sm font-bold mb-2 flex items-center gap-1">🔒 Per-Agency Allocation & Usage ({stats.per_agency.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {stats.per_agency.map(a => (
              <button
                key={a.agency_id}
                onClick={() => setFilterAgency(a.agency_id)}
                className={`text-left p-2 rounded border text-sm transition-colors ${filterAgency === a.agency_id ? 'bg-violet-50 border-violet-400' : 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100'}`}
                data-testid={`per-agency-${a.agency_id}`}
              >
                <div className="font-semibold">{a.agency_name}</div>
                <div className="text-[11px] text-gray-700 mt-0.5">
                  <span className="font-bold">{a.count}</span> cards · Limit {fmt(a.limit_total)}
                </div>
                <div className="text-[11px] mt-0.5">
                  <span className="text-emerald-700 font-bold">Bal: {fmt(a.balance_total)}</span>
                  {' · '}
                  <span className="text-amber-700 font-bold">Used: {fmt(a.used_total)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Per-Staff Allocation */}
      {stats?.per_staff?.length > 0 && (
        <div className="bg-white border rounded-lg p-3">
          <div className="text-sm font-bold mb-2 flex items-center gap-1"><UsersThree size={16} /> Per-Staff Allocation & Usage</div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {stats.per_staff.map(s => (
              <div key={s.staff_id} className="bg-blue-50 border border-blue-200 rounded p-2 text-sm" data-testid={`per-staff-${s.staff_id}`}>
                <div className="font-semibold">{s.staff_name}</div>
                <div className="text-[11px] text-gray-700 mt-0.5">
                  <span className="font-bold">{s.count}</span> cards · Limit {fmt(s.limit_total)}
                </div>
                <div className="text-[11px] mt-0.5">
                  <span className="text-emerald-700 font-bold">Balance: {fmt(s.balance_total)}</span>
                  {' · '}
                  <span className="text-amber-700 font-bold">Used: {fmt(s.used_total)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {['all', 'available', 'assigned', 'exhausted', 'blocked'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)} className={`text-xs px-3 py-1.5 rounded border font-medium capitalize ${filterStatus === s ? 'bg-violet-600 text-white border-violet-700' : 'bg-white text-gray-700 border-gray-300'}`} data-testid={`filter-${s}`}>{s}</button>
          ))}
        </div>
        <select
          value={filterAgency}
          onChange={(e) => setFilterAgency(e.target.value)}
          className="h-8 text-xs border rounded px-2 bg-white"
          data-testid="filter-agency"
          title="Filter by linked agency"
        >
          <option value="">All Agencies</option>
          <option value="unlinked">— Unlinked —</option>
          {agencies.map(a => (
            <option key={a.id} value={a.id}>🔒 {a.name}</option>
          ))}
        </select>
        {filterAgency && (
          <button onClick={() => setFilterAgency('')} className="text-[11px] text-red-600 hover:underline" data-testid="clear-agency-filter">✕ Clear</button>
        )}
        <Button size="sm" variant="outline" className="h-8 text-xs gap-1 bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100" onClick={exportExcel} data-testid="export-excel-btn">
          📥 Export Excel
        </Button>
        <div className="ml-auto">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search card / holder…"
            matchCount={cards.length}
            totalCount={cards.length}
            noun="cards"
            testId="card-search"
            inputClassName="pl-8 pr-7 h-8 text-sm w-64"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      <div className="flex items-center gap-2 bg-gray-50 border rounded p-2">
        <span className="text-xs text-gray-700">{selected.size} selected / {cards.length} shown</span>
        <Button size="sm" variant="outline" className="text-xs h-7" onClick={toggleAll}>Select All</Button>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs h-7 gap-1 ml-auto" disabled={selected.size === 0} onClick={() => setShowAssignDlg(true)} data-testid="bulk-assign-btn">
          <UserPlus size={14} /> Assign to Staff ({selected.size})
        </Button>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10px] uppercase text-gray-500">
            <tr>
              <th className="px-2 py-2 w-8 text-center"><input type="checkbox" checked={selected.size > 0 && selected.size === cards.length} onChange={toggleAll} /></th>
              <th className="px-2 py-2 text-left">#</th>
              <th className="px-2 py-2 text-left">Card</th>
              <th className="px-2 py-2 text-left">Holder</th>
              <th className="px-2 py-2 text-left">Expiry</th>
              <th className="px-2 py-2 text-left">CVV</th>
              <th className="px-2 py-2 text-right">Balance / Limit</th>
              <th className="px-2 py-2 text-center">Daily</th>
              <th className="px-2 py-2 text-left">Status</th>
              <th className="px-2 py-2 text-left">Assigned To</th>
              <th className="px-2 py-2 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={11} className="text-center text-gray-400 py-8">Loading…</td></tr>
              : cards.length === 0 ? <tr><td colSpan={11} className="text-center text-gray-400 py-8">Koi card nahi — Upload Cards button se add karo</td></tr>
              : cards.map((c, i) => (
                <tr key={c.id} className="border-t hover:bg-gray-50" data-testid={`card-row-${c.id}`}>
                  <td className="px-2 py-1 text-center"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggle(c.id)} /></td>
                  <td className="px-2 py-1 text-gray-500">{i + 1}</td>
                  <td className="px-2 py-1 font-mono text-xs">
                    **** **** **** {c.last4}
                    {c.linked_agency_name && (
                      <div className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1 inline-block mt-0.5" title={`Linked to ${c.linked_agency_name}`}>
                        🔒 {c.linked_agency_name}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1 text-xs">{c.card_holder || '—'}</td>
                  <td className="px-2 py-1 font-mono text-xs">{c.expiry || '—'}</td>
                  <td className="px-2 py-1 font-mono text-xs">{c.cvv ? '●●●' : '—'}</td>
                  <td className="px-2 py-1 text-right text-xs whitespace-nowrap">
                    <button
                      onClick={() => doAdjustBalance(c)}
                      className="inline-flex flex-col items-end gap-0 hover:bg-blue-50 px-1.5 py-0.5 rounded"
                      title="Click to adjust balance"
                      data-testid={`balance-cell-${c.id}`}
                    >
                      <span className={`font-bold ${Number(c.available_limit || 0) > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                        {fmt(c.available_limit)}
                      </span>
                      {c.initial_limit > 0 && c.initial_limit !== c.available_limit && (
                        <span className="text-[9px] text-gray-500">of {fmt(c.initial_limit)}</span>
                      )}
                    </button>
                  </td>
                  <td className="px-2 py-1 text-center text-xs">{c.daily_usage_count}/{c.daily_limit}</td>
                  <td className="px-2 py-1">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
                      c.status === 'available' ? 'bg-emerald-100 text-emerald-700' :
                      c.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                      c.status === 'sold' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                    }`}>{c.status}</span>
                  </td>
                  <td className="px-2 py-1">
                    <InlineStaffPicker
                      value={rowAssignments[c.id]}
                      onChange={(staff) => {
                        setRowAssignments(prev => ({ ...prev, [c.id]: staff }));
                        if (staff?.id && staff.id !== c.assigned_to_id) doInlineAssign(c.id, staff);
                      }}
                      staffList={staffList}
                      disabled={c.status === 'sold' || c.status === 'blocked'}
                      testId={`inline-assign-${c.id}`}
                    />
                  </td>
                  <td className="px-2 py-1 text-center">
                    <div className="flex gap-1 justify-center items-center">
                      <button
                        onClick={() => doSetCardPassword(c)}
                        className={`text-xs rounded p-0.5 ${c.card_password ? 'text-purple-700 bg-purple-50 hover:bg-purple-100' : 'text-gray-400 hover:bg-gray-100'}`}
                        title={c.card_password ? `Password set: ${c.card_password} · click to edit` : 'Set card login password'}
                        data-testid={`pwd-${c.id}`}
                      >🔑</button>
                      {c.assigned_to_id && (
                        <button onClick={() => doUnassign(c.id)} className="text-xs text-amber-700 hover:underline" title="Pull back to admin">⤺</button>
                      )}
                      <button onClick={() => doDelete(c.id)} className="text-red-600 hover:bg-red-50 rounded p-0.5" title="Delete" data-testid={`del-${c.id}`}><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Upload Dialog — block-format paste OR Excel */}
      <Dialog open={showUploadDlg} onOpenChange={(v) => { setShowUploadDlg(v); if (!v) { setUpText(''); setUpFile(null); setUpPreview(null); setUpAllowDupes(false); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard size={20} weight="duotone" className="text-violet-600" /> Upload Cards → Admin Direct Stock
            </DialogTitle>
          </DialogHeader>

          {/* Mode toggle */}
          <div className="flex gap-2">
            {[['manual', 'Paste Text', Plus], ['excel', 'Excel Upload', UsersThree]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => { setUpMode(k); setUpPreview(null); }} className={`flex-1 p-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 ${upMode === k ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-gray-200 bg-white'}`} data-testid={`up-mode-${k}`}>
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-[11px] text-emerald-900">
            ✅ Duplicates aur header lines (jaise "PUNEET INDANE") auto-skip ho jate hain. Multiple blank lines bhi handle hote hain. <b>No daily-limit cap.</b>
          </div>

          {/* Item 5: Link to agency (soft lock) */}
          <div className="bg-violet-50 border border-violet-200 rounded p-2" data-testid="up-link-agency">
            <label className="text-xs font-bold text-violet-900">🔒 Link to Specific Agency (optional)</label>
            <div className="text-[10px] text-violet-700 mb-1">
              Agar card kisi specific agency (jaise "JATAN BHARAT GAS") ke liye dedicated hai, to yahan select karo.
              Workspace me us agency ki bookings me yeh card auto-preferred hoga.
            </div>
            <select
              value={upLinkedAgency}
              onChange={(e) => setUpLinkedAgency(e.target.value)}
              className="w-full h-8 text-xs border rounded px-2 bg-white"
              data-testid="up-agency-select"
            >
              <option value="">-- Universal (koi bhi agency) --</option>
              {agencies.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name} · {a.oil_company} · ₹{a.rate}
                </option>
              ))}
            </select>
          </div>

          {/* Card Login Password (online password for these cards) */}
          <div className="bg-amber-50 border border-amber-200 rounded p-2" data-testid="up-card-password-block">
            <label className="text-xs font-bold text-amber-900">🔑 Card Login Password (optional)</label>
            <div className="text-[10px] text-amber-700 mb-1">
              In cards ka common login password. Staff/sub-staff ko workspace me card ke saath visible hoga.
            </div>
            <Input
              value={upPassword}
              onChange={(e) => setUpPassword(e.target.value)}
              placeholder="e.g. Card@1234"
              className="h-8 text-xs"
              data-testid="up-card-password-input"
            />
          </div>

          {/* Manual mode */}
          {upMode === 'manual' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700">Paste card details (any format — blank line separates cards)</label>
              <textarea
                rows={9}
                value={upText}
                onChange={(e) => setUpText(e.target.value)}
                placeholder={'Example:\nRAVI RAJ\n5321 3502 6437 5577\nCVV2 - 609\nExpiry - 04/26 (mm/yy)\nCard Limit Rs.20200\n\nVIVEK MUNOT\n6083 6702 0184 1217\n10/27\n083\nPin eE3cS1tD0\nRs 4988'}
                className="w-full border rounded p-2 font-mono text-xs"
                data-testid="up-text"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={submitPreview} disabled={upBusy} data-testid="up-preview-btn">Preview Parse</Button>
                <Button onClick={submitUpload} disabled={upBusy} className="bg-violet-600 hover:bg-violet-700" data-testid="up-submit-btn">
                  {upBusy ? 'Uploading…' : 'Upload to Admin Stock'}
                </Button>
              </div>
            </div>
          )}

          {/* Excel mode */}
          {upMode === 'excel' && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-700">Upload .xlsx (columns: Card Holder | Card Number | CVV | Expiry | PIN | Limit)</label>
              <Input type="file" accept=".xlsx,.xls" onChange={(e) => setUpFile(e.target.files?.[0] || null)} data-testid="up-file" />
              {upFile && <div className="text-[11px] text-gray-500">{upFile.name}</div>}
              <Button onClick={submitUpload} disabled={upBusy || !upFile} className="bg-violet-600 hover:bg-violet-700" data-testid="up-excel-submit">
                {upBusy ? 'Uploading…' : 'Upload to Admin Stock'}
              </Button>
            </div>
          )}

          {/* Preview table */}
          {upPreview && upPreview.parsed && upPreview.parsed.length > 0 && (
            <div className="border rounded-lg overflow-hidden mt-2">
              <div className="bg-violet-50 px-3 py-2 text-xs font-semibold flex justify-between">
                <span>Parsed: {upPreview.count} cards</span>
                <span>Total Face: {fmt(upPreview.total_face)}</span>
              </div>
              <div className="overflow-x-auto max-h-60">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[10px] uppercase">
                    <tr>
                      <th className="px-2 py-1 text-left">Holder</th>
                      <th className="px-2 py-1 text-left">Last 4</th>
                      <th className="px-2 py-1 text-left">CVV</th>
                      <th className="px-2 py-1 text-left">Expiry</th>
                      <th className="px-2 py-1 text-left">PIN</th>
                      <th className="px-2 py-1 text-right">Limit</th>
                      <th className="px-2 py-1 text-left">Flag</th>
                    </tr>
                  </thead>
                  <tbody>
                    {upPreview.parsed.map((p, idx) => (
                      <tr key={idx} className={`border-t ${p.is_duplicate_existing ? 'bg-red-50' : p.is_duplicate_within_batch ? 'bg-amber-50' : ''}`}>
                        <td className="px-2 py-1">{p.card_holder || '—'}</td>
                        <td className="px-2 py-1 font-mono">****{p.card_number_last4}</td>
                        <td className="px-2 py-1">{p.cvv || '—'}</td>
                        <td className="px-2 py-1">{p.expiry || '—'}</td>
                        <td className="px-2 py-1">{p.pin || '—'}</td>
                        <td className="px-2 py-1 text-right">{fmt(p.card_limit)}</td>
                        <td className="px-2 py-1">
                          {p.is_duplicate_existing ? <span className="text-red-700 font-semibold text-[10px]">DB DUPE</span>
                            : p.is_duplicate_within_batch ? <span className="text-amber-700 font-semibold text-[10px]">BATCH DUPE</span>
                            : <span className="text-emerald-700 text-[10px]">OK</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {upPreview.existing_duplicates?.length > 0 && (
                <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 text-xs text-amber-900">
                  ⚠️ {upPreview.existing_duplicates.length} cards pehle se DB me hain. "Skip duplicates" check karke retry karo.
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDlg(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={showAssignDlg} onOpenChange={setShowAssignDlg}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus size={18} className="text-blue-600" /> Assign {selected.size} Cards to Main Staff</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900">
              ℹ️ Admin sirf <b>main staff</b> ko card assign kar sakta hai. Staff apni team (sub-staff) ko khud distribute karega <code>My Cards (Team)</code> page se.
            </div>
            <Input placeholder="Main staff naam type karo…" value={assignStaffInput} onChange={(e) => { setAssignStaffInput(e.target.value); setAssignStaff(null); }} data-testid="assign-staff-input" />
            <div className="max-h-64 overflow-y-auto space-y-1">
              {filteredStaff(assignStaffInput).map(s => (
                <div key={s.id} onClick={() => { setAssignStaff(s); setAssignStaffInput(s.name); }} className={`p-2 rounded border cursor-pointer ${assignStaff?.id === s.id ? 'bg-blue-50 border-blue-400' : 'bg-white border-gray-200 hover:bg-gray-50'}`}>
                  <div className="text-sm font-medium">{s.name}</div>
                  <div className="text-[11px] text-gray-500">{s.email} · {s.role}</div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDlg(false)}>Cancel</Button>
            <Button onClick={submitAssign} className="bg-blue-600 hover:bg-blue-700" disabled={!assignStaff} data-testid="confirm-assign-btn">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, count, sub, color }) {
  const bg = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    gray: 'bg-gray-100 border-gray-300 text-gray-700',
    red: 'bg-red-50 border-red-200 text-red-800',
  }[color] || 'bg-gray-50';
  return (
    <div className={`${bg} border rounded-lg p-3`}>
      <div className="flex items-center gap-1 text-[11px] uppercase font-semibold">{icon} {label}</div>
      <div className="text-xl font-bold mt-1">{count}</div>
      <div className="text-[10px] opacity-75">{sub}</div>
    </div>
  );
}

function InlineStaffPicker({ value, onChange, staffList, disabled, testId }) {
  const [input, setInput] = useState(value?.name || '');
  const [open, setOpen] = useState(false);
  useEffect(() => { setInput(value?.name || ''); }, [value]);
  const matches = !input ? staffList.slice(0, 6) :
    staffList.filter(s => s.name.toLowerCase().includes(input.toLowerCase())).slice(0, 6);
  return (
    <div className="relative">
      <input
        type="text"
        value={input}
        onChange={(e) => { setInput(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        disabled={disabled}
        placeholder="Type name…"
        className="w-full px-2 py-1 border rounded text-xs h-7 disabled:bg-gray-100"
        data-testid={testId}
      />
      {open && matches.length > 0 && (
        <div className="absolute z-10 mt-1 bg-white border rounded shadow-lg max-h-48 overflow-y-auto w-48">
          {matches.map(s => (
            <div key={s.id} onMouseDown={(e) => { e.preventDefault(); onChange(s); setOpen(false); }} className="px-2 py-1.5 hover:bg-blue-50 cursor-pointer text-xs border-b last:border-b-0">
              <div className="font-medium">{s.name}</div>
              <div className="text-[10px] text-gray-500">{s.role}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
